import React, { FC, useEffect, useState } from "react"

import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, LoadingOutlined } from "@ant-design/icons"
import { Button, Modal, Space, Table, Tooltip } from "antd"
import { ColumnsType } from "antd/es/table"

import AddModal from "./AddModal"
import EditModal from "./EditModal"
import { successTagColor, errorTagColor, allTagColors } from "../../../constants/colors"
import { useActions } from "../../../hooks/useActions"
import { useTypedSelector } from "../../../hooks/useTypedSelector"
import ColorTag from "../../ui/color-tag/ColorTag"

import "./StaffPage.scss"

const StaffPage: FC = () => {
    const [addModalOpen, setAddModalOpen] = useState(false)
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [editId, setEditId] = useState(0)
    const [isLoading, setIsLoading] = useState(false)

    const { staffData } = useTypedSelector((state) => state.staff)
    const { staffFetchData, staffDelete, currentUserGetStatus } = useActions()

    const getColor = (index: number) => {
        return allTagColors[index % allTagColors.length]
    }

    useEffect(() => {
        // Загрузка данных при монтировании
        setIsLoading(true)
        staffFetchData(() => { setIsLoading(false) })

        // Интервал для обновления статуса (каждые 5 секунд)
        const statusInterval = setInterval(() => {
            currentUserGetStatus()
        }, 5000)

        // Очистка интервала при размонтировании
        return () => {
            clearInterval(statusInterval)
        }
    }, [])

    // Рендер статуса с цветным тегом
    const renderStatus = (value: string) => (
        <span className="status-tag">
            {value === "Готов" || value === "Не в сети" ? (
                <ColorTag color={value === "Готов" ? successTagColor : errorTagColor}>
                    {value}
                </ColorTag>
            ) : (
                <ColorTag defaultColor>
                    {value}
                </ColorTag>
            )}
        </span>
    )

    const staffColumns: ColumnsType<any> = [
        {
            title: "Логин",
            dataIndex: "username",
            key: "username",
            width: 80,
        },
        {
            title: "ФИО",
            dataIndex: "name",
            key: "name",
            width: 110,
        },
        {
            title: "Группа",
            dataIndex: "group",
            key: "group",
            width: 110,
        },
        {
            title: "Статус",
            dataIndex: "status",
            key: "status",
            width: 110,
            render: renderStatus
        },
        {
            title: "WS",
            dataIndex: "ws_status",
            key: "ws_status",
            width: 100,
            render: renderStatus
        },
        {
            title: "ЦИАН",
            dataIndex: "cian_auth_status",
            key: "cian_auth_status",
            width: 100,
            render: (value: number) => (
                <span className="status-tag">
                    <ColorTag color={value === 1 ? successTagColor : errorTagColor}>
                        {value === 1 ? "Авториз." : "Не авториз."}
                    </ColorTag>
                </span>
            )
        },
        {
            title: "Роль",
            dataIndex: "role",
            key: "role",
            width: 95,
        },
        {
            title: "IP",
            dataIndex: "ip_address",
            key: "ip_address",
            width: 130,
        },
        {
            title: "Локации",
            dataIndex: "locations",
            key: "locations",
            render: (value) => {
                const items = `${value}`?.split(", ")?.filter(item => item !== "null") || []

                if (items.length === 0) return null

                // Показываем первые 3 тега, остальные в тултипе
                const visibleItems = items.slice(0, 3)
                const hiddenItems = items.slice(3)

                return (
                    <div className="locations-cell">
                        {visibleItems.map((item, index) => (
                            <ColorTag key={index} color={getColor(index)}>
                                {item}
                            </ColorTag>
                        ))}
                        {hiddenItems.length > 0 && (
                            <Tooltip
                                title={hiddenItems.join(", ")}
                                placement="top"
                            >
                                <span>
                                    <ColorTag defaultColor>
                                        +{hiddenItems.length}
                                    </ColorTag>
                                </span>
                            </Tooltip>
                        )}
                    </div>
                )
            }
        },
        {
            title: "",
            dataIndex: "actions",
            key: "actions",
            width: 80,
            render: (value, record) => (
                <Space size={4}>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => {
                            setEditId(record.id)
                            setEditModalOpen(true)
                        }}
                    />
                    <Button
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => {
                            Modal.confirm({
                                title: "Вы уверены что хотите удалить сотрудника?",
                                icon: <ExclamationCircleOutlined />,
                                okText: "Да",
                                okType: "danger",
                                cancelText: "Нет",
                                onOk() {
                                    staffDelete(record.id, () => {
                                        staffFetchData()
                                    })
                                },
                            })
                        }}
                        danger
                    />
                </Space>
            )
        },
    ]

    return (
        <div className="page-content">
            <div style={{ marginBottom: 16 }}>
                <Button
                    type="primary"
                    onClick={() => setAddModalOpen(true)}
                >
                    Добавить сотрудника
                </Button>
            </div>

            <Table
                className="staff-table"
                dataSource={staffData}
                columns={staffColumns}
                size="middle"
                scroll={{ x: 1000, y: 'calc(100vh - 280px)' }}
                pagination={false}
                rowKey="id"
                loading={{
                    spinning: isLoading,
                    indicator: <LoadingOutlined style={{ fontSize: 24 }} spin />
                }}
            />

            <AddModal
                open={addModalOpen}
                setOpen={setAddModalOpen}
            />

            <EditModal
                id={editId}
                open={editModalOpen}
                setOpen={setEditModalOpen}
            />
        </div>
    )
}

export default StaffPage
